import { getSdgDisplayName, getSdgNumberFromDisplayName, isSdgDisplayName } from './sdgs';

/**
 * Centralized labels for Investor Catalog categories.
 * Used in category rows, header content, and profile badge.
 */
export const CATALOG_CATEGORY_LABELS = {
  speciallyForYou: 'Specially for you',
  basedOnYourFavorites: 'Based on your Favorites',
  previouslyViewed: 'Previously viewed',
  yourFavorites: 'Your Favorites',
  recentlyAdded: 'Recently added',
  womenLed: 'Women-led',
  pocOwned: 'POC owned',
  lgbtqOwned: 'LGBTQ+ owned',
  sdgAligned: 'Aligned with your selected SDGs',
  mostImpactStartups: 'Most Impact Startups',
  europeBased: 'Europe based',
  usBased: 'US-based',
  northAmericaBased: 'North America-based',
  latamBased: 'LATAM-based',
  nordicsBased: 'Nordics-based',
  africaBased: 'Africa-based',
  moreStartups: 'More Startups',
  featuredStartup: 'Featured Startup',
  mostPopular: 'Most Popular',
  nearYou: 'Near you',
  otherIndustriesInYourLocation: 'Other industries in your preferred locations',
  yourIndustriesInOtherLocations: 'Your industries in other locations',
} as const;

export type CatalogCategoryKey = keyof typeof CATALOG_CATEGORY_LABELS;

/** Display label for a fixed catalog category */
export type CatalogCategoryLabel = (typeof CATALOG_CATEGORY_LABELS)[CatalogCategoryKey];

/** Category to display: fixed label or dynamic string */
export type CatalogCategory = CatalogCategoryLabel | string;

/**
 * Catalog row title under Favorites for similar startups.
 * Profile badge uses {@link getSinceYouFollowBadgeLabel} instead.
 */
export function getSinceYouAddedFavoritesLabel(_companyName?: string): string {
  return CATALOG_CATEGORY_LABELS.basedOnYourFavorites;
}

/** Profile category badge only — not the catalog row title. */
export function getSinceYouFollowBadgeLabel(companyName?: string): string {
  const name = companyName?.trim();
  if (name) return `Since you follow ${name}`;
  return CATALOG_CATEGORY_LABELS.basedOnYourFavorites;
}

/** Map legacy row/badge titles onto the Follow badge copy. */
export function normalizeSinceYouFollowLabel(title: string): string {
  const legacy = title.match(/^Since you added (.+) to your Favorites$/i);
  if (legacy?.[1]?.trim()) return getSinceYouFollowBadgeLabel(legacy[1]);
  return title;
}

export function isSinceYouAddedFavoritesTitle(title: string): boolean {
  return (
    title === CATALOG_CATEGORY_LABELS.basedOnYourFavorites ||
    title.startsWith('Since you follow ') ||
    title.startsWith('Since you added ')
  );
}

/** Industry row from favorites / preferences */
export function getIndustryCatalogLabel(industryDisplayName: string): string {
  return industryDisplayName;
}

/** Strip catalog continent titles like "US-based" / "Europe based" → "US" / "Europe". */
export function continentTitleToPlaceName(continentTitle: string): string {
  return continentTitle
    .replace(/-based$/i, '')
    .replace(/\s+based$/i, '')
    .trim();
}

/**
 * Alice join rules for LI multi-continent titles:
 * - 1: North America
 * - 2: North America, and South America
 * - 3+: North America, South America and Europe
 */
export function formatContinentListForLiTitle(continentTitles: string[]): string {
  const places = continentTitles.map(continentTitleToPlaceName).filter(Boolean);
  if (places.length === 0) return '';
  if (places.length === 1) return places[0];
  if (places.length === 2) return `${places[0]}, and ${places[1]}`;
  return `${places.slice(0, -1).join(', ')} and ${places[places.length - 1]}`;
}

/**
 * Industry × Continent LI row title.
 * Alice: "{industry} based in {continent(s)}"
 * e.g. "Deep Tech based in North America, and South America"
 */
export function getLiCatalogLabel(
  industryDisplayName: string,
  continentTitles: string | string[]
): string {
  const titles = Array.isArray(continentTitles) ? continentTitles : [continentTitles];
  const places = formatContinentListForLiTitle(titles);
  if (!industryDisplayName) return places;
  if (!places) return industryDisplayName;
  return `${industryDisplayName} based in ${places}`;
}

/** True when a catalog title is an Industry×Continent LI row. */
export function isLiCatalogCategory(category: string): boolean {
  return /\sbased in\s/i.test(category);
}

/** Investor home-country row */
export function getStartupCountryCatalogLabel(country: string): string {
  return `Startups from ${country}`;
}

/** Near you = investor country when it is also in their preferred countries */
export function getNearYouLabel(country?: string): string {
  return country ? `Near you · ${country}` : CATALOG_CATEGORY_LABELS.nearYou;
}

/**
 * SDG feed title: "SDG: {goal name}".
 * Falls back to Most Impact Startups when no goal is resolved.
 */
export function getWorkingTowardsSdgLabel(sdgNumber?: number): string {
  if (sdgNumber != null) {
    const name = getSdgDisplayName(sdgNumber);
    if (name) return `SDG: ${name}`;
  }
  return CATALOG_CATEGORY_LABELS.mostImpactStartups;
}

/** Parse SDG goal number from a catalog/badge title (new or legacy formats). */
export function parseSdgNumberFromCatalogCategory(category: string): number | null {
  const sdgPrefix = category.match(/^SDG:\s*(.+)$/);
  if (sdgPrefix?.[1]) {
    return getSdgNumberFromDisplayName(sdgPrefix[1]);
  }
  const workingToward = category.match(/^Working toward '(.+)'$/);
  if (workingToward?.[1]) {
    return getSdgNumberFromDisplayName(workingToward[1]);
  }
  if (isSdgDisplayName(category)) {
    return getSdgNumberFromDisplayName(category);
  }
  return null;
}

/** Near you / Near you · {country} */
export function isNearYouCatalogCategory(category: string): boolean {
  return (
    category === CATALOG_CATEGORY_LABELS.nearYou ||
    category.startsWith(`${CATALOG_CATEGORY_LABELS.nearYou} ·`)
  );
}

/** True when a catalog/badge title is an SDG impact row (selected or default). */
export function isSdgCatalogCategory(category: string): boolean {
  return (
    category === CATALOG_CATEGORY_LABELS.sdgAligned ||
    category === CATALOG_CATEGORY_LABELS.mostImpactStartups ||
    category.startsWith('Most SDG') ||
    category.startsWith('Working towards SDG') ||
    category.startsWith('SDG: ') ||
    category.startsWith("Working toward '") ||
    isSdgDisplayName(category)
  );
}
